import { useEffect, useState } from "react";
import PageLoader from "../../../components/PageLoader";
import { toast } from "react-toastify";
import { httprequest } from "../../../data/api";
import { useNavigate } from "react-router-dom";
import { useMediaQuery, Box, Tabs, Tab, Grid } from "@mui/material";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import Layout from "../../../components/Layout";
import PageTitle from "../../../components/PageTitle";
import Button from "../../../components/Button";
import Table from "../../../components/Table/Table";
import { TableSearch } from "../../../components/Table/TableActions";
import { Icon } from "@iconify/react/dist/iconify.js";
import Modal from "../../../components/Modal";
import Select from "react-select";
import useValidator from "../../../hooks/useValidator";
import InputGroup from "../../../components/FormComponent/InputGroup";
import styles from "../report.module.css";

const REPORT_ENDPOINT = "/api/v1/custom-reports";
const CLASSES_ENDPOINT = "/api/v1/class";
const GET_SESSIONS = "/api/v1/school-plan/session";
const TERM_ENDPOINT = "/api/v1/school-plan/terms";
const GELL_ALL_SUBJECT_ENDPOINT = "/api/v1/subjects";
const OBTAINABLE_SCORE = "/api/v1/obtainable-score";
const STUDENTS_ENDPOINT = "/api/v1/students";
const GRADING_SYSTEMS_ENDPOINT = "/api/v1/grading-system";

const CustomReport = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [reportData, setReportData] = useState([]);
  const [selectedTab, setSelectedTab] = useState("student");
  const [filteredData, setFilteredData] = useState([]);
  const navigate = useNavigate();
  const [isReportLoading, setIsReportLoading] = useState(false);

  // Modal state variables
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [classesData, setClassData] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [selectedSessionTerms, setSelectedSessionTerms] = useState([]);
  const [selectedSession, setSelectedSession] = useState("");
  const [aselectedTerm, setASelectedTerm] = useState("");

  // Subject State for React-Select
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);

  const [obtainablescore, setObtainableScore] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [reportTarget, setReportTarget] = useState("class");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [title, setTitle] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [reportValidator, showCreateValidationMessage] = useValidator();
  const [gradingSystems, setGradingSystems] = useState([]);
  const [selectedGradingSystem, setSelectedGradingSystem] = useState("");
  const [extraColumns, setExtraColumns] = useState({
    exam: false,
    grade: false
  });

  const getAllClasses = () => {
    httprequest
      .get(CLASSES_ENDPOINT)
      .then((res) => {
        const physicalClasses = res?.data?.data?.filter(
          (data) => data?.class_type === "physical"
        );
        setClassData(physicalClasses);
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  const getAllSessions = async () => {
    try {
      const res = await httprequest.get(GET_SESSIONS);
      setAllSessions(res?.data?.data);
    } catch (err) {
      toast.error(err?.response?.message);
    }
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    // Reset form data
    setTitle("");
    setSelectedClass("");
    setSelectedSession("");
    setASelectedTerm("");
    setReportTarget("class");
    setSelectedStudent(null);
    setSelectedGradingSystem("");
    setSelectedSubjects([]);
    setObtainableScore((prev) => [
      {
        ...prev[0],
        ca: prev[0]?.ca?.map((ca) => ({ ...ca, selected: false })) || []
      }
    ]);
    setExtraColumns({
      exam: false,
      grade: false
    });
  };

  const getAllTerms = (Session_ID) => {
    httprequest
      .get(TERM_ENDPOINT)
      .then((res) => {
        const filteredTerms = res?.data?.data?.filter(
          (term) => term?.session_id === Session_ID
        );
        setSelectedSessionTerms(filteredTerms || []);
      })
      .catch((err) => {
        toast.error(err?.response?.data || "Failed to fetch terms");
      });
  };

  const getAllSubjects = () => {
    httprequest
      .get(GELL_ALL_SUBJECT_ENDPOINT)
      .then((res) => {
        const formattedSubjects = res?.data?.data?.map((sub) => ({
          label: sub.name,
          value: sub.id
        }));
        setSubjectOptions(formattedSubjects);
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  const getAllStudents = () => {
    httprequest
      .get(STUDENTS_ENDPOINT)
      .then((res) => {
        setAllStudents(
          res?.data?.data?.map((student) => {
            return {
              label: `${student?.student?.first_name} ${student?.student?.last_name}`,
              value: student?.student?.id
            };
          })
        );
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  const getAllObtainableScores = () => {
    httprequest
      .get(OBTAINABLE_SCORE)
      .then((res) => {
        setObtainableScore(
          res?.data?.data?.filter(
            (scores) => scores?.session?.id === selectedSession
          )
        );
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
      });
  };

  const getAllGradingSystems = () => {
    httprequest
      .get(GRADING_SYSTEMS_ENDPOINT)
      .then((res) => {
        setGradingSystems(res?.data?.data || []);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || "Failed to fetch grading systems"
        );
      });
  };

  const getAllReports = () => {
    setIsReportLoading(true);
    httprequest
      .get(REPORT_ENDPOINT)
      .then((res) => {
        const reports = res?.data?.data;
        setReportData(reports);
        // Filter for student reports initially
        const studentReports =
          reports?.filter((report) => report.type === "student") || [];
        setFilteredData(studentReports);
      })
      .catch((err) => {
        toast.error(err?.response?.message);
      })
      .finally(() => {
        setIsReportLoading(false);
      });
  };

  const handleSessionChange = (e) => {
    const Session_ID = e.target.value;
    setSelectedSession(Session_ID);
    getAllTerms(Session_ID);
    getAllObtainableScores();
  };

  const handleTermChange = (event) => {
    const selectedTermId = event.target.value;
    setASelectedTerm(selectedTermId);
  };

  const handleStudentChange = (selectedOption) => {
    setSelectedStudent(selectedOption?.value || "");
  };

  const proceedToCreateReport = () => {
    setIsLoading(true);

    // Get IDs from react-select state
    const selectedSubjectIds = selectedSubjects.map(s => s.value);

    // Build columns array from obtainable scores and extra columns
    const selectedColumns = obtainablescore?.[0]?.ca
      ?.filter((ca) => ca.selected)
      ?.map((ca) => ca.ca_type) || [];

    if (extraColumns.exam) {
      selectedColumns.push("exam");
    }
    if (extraColumns.grade) {
      selectedColumns.push("grade");
    }

    const reportData = {
      title,
      term_id: aselectedTerm,
      subjects: selectedSubjectIds,
      columns: selectedColumns,
      type: reportTarget
      // grading_system_id: selectedGradingSystem
    };

    if (reportTarget === "class") {
      reportData.class_id = selectedClass;
    }

    if (reportTarget === "student") {
      reportData.student_id = selectedStudent;
    }

    httprequest
      .post(REPORT_ENDPOINT, reportData)
      .then((res) => {
        toast.success(res.data?.message || "Report created successfully");
        handleCloseCreateModal();
        getAllReports(); // Refresh the reports list
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Failed to create report");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCreateReport = () => {
    if (selectedSubjects.length === 0) {
      toast.error("Please select at least one subject");

      return;
    }
    reportValidator.allValid()
      ? proceedToCreateReport()
      : showCreateValidationMessage(true);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    // Filter reports based on selected tab
    if (newValue === "student") {
      const studentReports =
        reportData?.filter((report) => report.type === "student") || [];
      setFilteredData(studentReports);
    } else if (newValue === "class") {
      const classReports =
        reportData?.filter((report) => report.type === "class") || [];
      setFilteredData(classReports);
    }
  };

  const handleSearch = (query) => {
    if (!query.trim()) {
      // Reset to current tab's data when search is cleared
      const currentData =
        selectedTab === "student"
          ? reportData?.filter((report) => report.type === "student") || []
          : reportData?.filter((report) => report.type === "class") || [];
      setFilteredData(currentData);

      return;
    }

    const currentData =
      selectedTab === "student"
        ? reportData?.filter((report) => report.type === "student") || []
        : reportData?.filter((report) => report.type === "class") || [];

    const filtered = currentData.filter(
      (item) => item.title?.toLowerCase().includes(query.toLowerCase()) ||
        item.target?.name?.toLowerCase().includes(query.toLowerCase()) ||
        item.target?.first_name?.toLowerCase().includes(query.toLowerCase()) ||
        item.target?.last_name?.toLowerCase().includes(query.toLowerCase()) ||
        item.gennerated_by?.first_name
          ?.toLowerCase()
          .includes(query.toLowerCase()) ||
        item.gennerated_by?.last_name
          ?.toLowerCase()
          .includes(query.toLowerCase())
    );
    setFilteredData(filtered);
  };

  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
    getAllSessions();
    getAllClasses();
    getAllStudents();
    getAllSubjects();
    getAllGradingSystems();
  };

  // ... (Keep existing columns definition) ...
  const columns = [
    {
      name: "Report Title",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Report Title</p>}
          {row.title}
        </>
      )
    },
    {
      name: "Target",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Target</p>}
          {row.target?.name || `${row.target?.last_name || ""} ${row.target?.first_name || ""}`}
        </>
      )
    },
    {
      name: "Type",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Type</p>}
          {row.type}
        </>
      )
    },
    {
      name: "Generated By",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Generated By</p>}
          {row.gennerated_by.last_name} {row.gennerated_by.first_name}
        </>
      )
    },
    {
      name: "Date Generated",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Date Generated</p>}
          {row.created_at.slice(0, 10) || "--/--"}
        </>
      )
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          <button className="table-action-icon" onClick={() => navigate(
            row.type === "student"
              ? `/report/custom/view/${row.id}/${row?.target?.id}`
              : `/report/custom/class/${row.id}`
          )
          }
          >
            <Icon icon="hugeicons:view" />
          </button>
        </div>
      )
    }
  ];

  useEffect(() => {
    getAllReports();
  }, []);

  useEffect(() => {
    if (selectedSession) {
      getAllTerms(selectedSession);
      getAllObtainableScores();
    }
  }, [selectedSession]);

  return (
    <Layout
      children={
        <>
          <PageTitle
            title="Custom Reports"
            subtitle="Set the right filters to generate the right report."
            button={
              <div className="flex gap-10">
                <Button
                  variant="primary"
                  buttonSize="fluid"
                  color="btndefault"
                  buttonName="Create Report"
                  onClick={handleOpenCreateModal}
                />
              </div>
            }
          />

          <div className="page-content margin-top-30">
            {isReportLoading ? (
              <PageLoader />
            ) : (
              <div>
                <Box>
                  <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="custom report tabs"
                  >
                    <Tab
                      key="student"
                      value="student"
                      label="Student Reports"
                      sx={{ textTransform: "none" }}
                    />
                    <Tab
                      key="class"
                      value="class"
                      label="Class Reports"
                      sx={{ textTransform: "none" }}
                    />
                  </Tabs>

                  <Box sx={{ mt: 2 }}>
                    <Table
                      data={filteredData}
                      columns={columns}
                      subHeader={true}
                      pagination
                      subHeaderComponent={
                        <div className="action_div">
                          <TableSearch
                            placeholder="Search here..."
                            searchTableFunc={handleSearch}
                          />
                          <SelectGroup
                            children={
                              <select>
                                <option value="">Batch Actions</option>
                                <option value="publish">
                                  Batch Publish Reports
                                </option>
                                <option value="publish_send">
                                  Batch Publish and Send Reports
                                </option>
                                <option value="print">
                                  Batch Print Reports
                                </option>
                                <option value="ready">
                                  Batch Ready to Publish Reports
                                </option>
                              </select>
                            }
                          />
                        </div>
                      }
                    />
                  </Box>
                </Box>
              </div>
            )}
          </div>

          <Modal
            title="Create report for students, individually or for a whole class."
            subtitle=""
            isopen={createModalOpen}
            closeModal={handleCloseCreateModal}
            modalContent={
              <div className="modal-form-container" style={{ padding: "10px" }}>
                <Grid container spacing={2}>

                  <Grid item xs={12}>
                    <div className="radio-group" style={{ display: "flex", gap: "20px", marginTop: "10px" }}>
                      <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                        <input
                          type="radio"
                          name="target"
                          value="class"
                          checked={reportTarget === "class"}
                          onChange={() => setReportTarget("class")}
                          style={{ width: "18px", height: "18px" }}
                        />
                        <span>Class</span>
                      </label>

                      <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                        <input
                          type="radio"
                          name="target"
                          value="student"
                          checked={reportTarget === "student"}
                          onChange={() => setReportTarget("student")}
                          style={{ width: "18px", height: "18px" }}
                        />
                        <span>Student</span>
                      </label>
                    </div>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InputGroup
                      label="Report Name"
                      required
                      inputType="text"
                      inputName="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                    {reportValidator.message("title", title, "required")}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {reportTarget === "student" ? (
                      <div className="multiple-select">
                        <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>Student</label>
                        <Select
                          onChange={handleStudentChange}
                          name="student"
                          closeMenuOnSelect={true}
                          options={allStudents}
                          placeholder="Select Student"
                        />
                        {reportValidator.message("selectedStudent", selectedStudent, "required")}
                      </div>
                    ) : (
                      <SelectGroup
                        label="Select Class"
                        children={
                          <select
                            name="class"
                            value={selectedClass}
                            onChange={(e) => setSelectedClass(e.target.value)}
                          >
                            <option value="">--Select Class--</option>
                            {classesData?.map((data) => (
                              <option key={data?.id} value={data?.id}>
                                {data?.class_name}
                              </option>
                            ))}
                          </select>
                        }
                      />
                    )}
                    {reportTarget === "class" && reportValidator.message("class_id", selectedClass, "required")}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SelectGroup
                      label="Select Session"
                      children={
                        <select
                          onChange={handleSessionChange}
                          value={selectedSession}
                          name="session"
                        >
                          <option value="">--Select--</option>
                          {allSessions?.map((sesh) => (
                            <option key={sesh?.id} value={sesh?.id}>
                              {sesh?.session}
                            </option>
                          ))}
                        </select>
                      }
                    />
                    {reportValidator.message("session_id", selectedSession, "required")}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SelectGroup
                      label="Select Term"
                      children={
                        <select
                          name="term"
                          value={aselectedTerm}
                          onChange={handleTermChange}
                        >
                          <option value="">--Select--</option>
                          {selectedSessionTerms?.map((data) => (
                            <option key={data?.id} value={data?.id}>
                              {data?.term}
                            </option>
                          ))}
                        </select>
                      }
                    />
                    {reportValidator.message("term_id", aselectedTerm, "required")}
                  </Grid>

                  <Grid item xs={12}>
                    <div className="multiple-select">
                      <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>Subject(s)</label>
                      <Select
                        isMulti
                        name="subjects"
                        options={subjectOptions}
                        value={selectedSubjects}
                        onChange={setSelectedSubjects}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="Select Subject(s)"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999
                          })
                        }}
                      />
                      {selectedSubjects.length === 0 && <span style={{ color: "red", fontSize: "10px", display: "none" }} className="validation-message">At least one subject is required</span>}
                    </div>
                  </Grid>

                  <Grid item xs={12}>
                    <SelectGroup
                      label="Select Grading System"
                      children={
                        <select
                          name="grading_system"
                          value={selectedGradingSystem}
                          onChange={(e) => setSelectedGradingSystem(e.target.value)
                          }
                        >
                          <option value="">--Select--</option>
                          {gradingSystems?.map((system) => (
                            <option key={system?.id} value={system?.id}>
                              {system?.title}
                            </option>
                          ))}
                        </select>
                      }
                    />
                    {reportValidator.message("grading_system_id", selectedGradingSystem, "required")}
                  </Grid>

                  <Grid item xs={12}>
                    <div className={styles.subjects_container}>
                      {/* Header */}
                      <label className={styles.subjects_header}>
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked) {
                              // select all columns
                              setObtainableScore((prev) => [
                                {
                                  ...prev[0],
                                  ca: prev[0]?.ca?.map((ca) => ({ ...ca, selected: true }))
                                }
                              ]);
                              setExtraColumns({ exam: true, grade: true });
                            } else {
                              // unselect all columns
                              setObtainableScore((prev) => [
                                {
                                  ...prev[0],
                                  ca: prev[0]?.ca?.map((ca) => ({ ...ca, selected: false }))
                                }
                              ]);
                              setExtraColumns({ exam: false, grade: false });
                            }
                          }}
                          checked={
                            (obtainablescore?.[0]?.ca?.length > 0 &&
                              obtainablescore[0].ca.every((ca) => ca.selected)) &&
                            extraColumns.exam &&
                            extraColumns.grade
                          }
                        />
                        <span>Select Columns</span>
                      </label>

                      {/* List of columns */}
                      <div className={styles.subjects_list}>
                        {/* Obtainable scores */}
                        {obtainablescore?.[0]?.ca?.map((ca) => (
                          <label key={ca?.ca_type} className={styles.subject_item}>
                            <input
                              type="checkbox"
                              checked={!!ca?.selected}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setObtainableScore((prev) => [
                                  {
                                    ...prev[0],
                                    ca: prev[0]?.ca?.map((c) => c.ca_type === ca.ca_type ? { ...c, selected: checked } : c
                                    )
                                  }
                                ]);
                              }}
                            />
                            <span>{ca.ca_type}</span>
                          </label>
                        ))}

                        {/* Exam */}
                        <label className={styles.subject_item}>
                          <input
                            type="checkbox"
                            checked={extraColumns.exam}
                            onChange={(e) => setExtraColumns((prev) => ({
                              ...prev,
                              exam: e.target.checked
                            }))
                            }
                          />
                          <span>Exam</span>
                        </label>

                        {/* Grade */}
                        <label className={styles.subject_item}>
                          <input
                            type="checkbox"
                            checked={extraColumns.grade}
                            onChange={(e) => setExtraColumns((prev) => ({
                              ...prev,
                              grade: e.target.checked
                            }))
                            }
                          />
                          <span>Grade</span>
                        </label>
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </div>
            }
            modalFooter={
              <div className="flex gap-10" style={{ justifyContent: "flex-end", width: "100%" }}>
                <Button
                  variant="primary"
                  // buttonSize="fluid"
                  color="btndefault"
                  buttonName="Save"
                  onClick={handleCreateReport}
                  isLoading={isLoading}
                  style={{ minWidth: "100px" }}
                />
              </div>
            }
          />
        </>
      }
    />
  );
};

export default CustomReport;
